/**
 * product-packaging.js
 *
 * Disables the "Signature Box" packaging option automatically
 * whenever the "Standard" packaging variant is out of stock.
 *
 * Requires two globals to already be set on the page BEFORE this
 * file loads (theme.liquid still needs to output these, since they
 * depend on Liquid/product data):
 *
 *   window.AFI_PRODUCT_VARIANTS = [ ... ]
 *   window.AFI_PRODUCT_OPTIONS  = [ ... ]
 */

document.addEventListener('DOMContentLoaded', function () {
  const variants = window.AFI_PRODUCT_VARIANTS || [];
  const productOptions = window.AFI_PRODUCT_OPTIONS || [];

  let updateTimeout = null;
  let isProcessing = false;

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function getPackagingWrapper() {
    return document.querySelector(
      '.variant-wrapper.packaging, ' +
      '.variant-wrapper[data-type="button"] fieldset[data-handle="packaging"]'
    );
  }

  function getActualWrapper() {
    const element = getPackagingWrapper();

    if (!element) return null;

    return element.matches('.variant-wrapper')
      ? element
      : element.closest('.variant-wrapper');
  }

  function getPackagingInput(value) {
    const wrapper = getActualWrapper();

    if (!wrapper) return null;

    return Array.from(
      wrapper.querySelectorAll('input[type="radio"]')
    ).find(function (input) {
      return normalize(input.value) === normalize(value);
    }) || null;
  }

  function getSelectedOptionValue(optionName, optionIndex) {
    const expectedIndex = 'option' + (optionIndex + 1);

    const checkedByIndex = document.querySelector(
      'input[data-index="' + expectedIndex + '"]:checked'
    );

    if (checkedByIndex) {
      return checkedByIndex.value;
    }

    const radioInputs = Array.from(
      document.querySelectorAll('input[type="radio"]:checked')
    );

    const checkedByName = radioInputs.find(function (input) {
      return normalize(input.name) === normalize(optionName);
    });

    if (checkedByName) {
      return checkedByName.value;
    }

    const selectByIndex = document.querySelector(
      'select[data-index="' + expectedIndex + '"]'
    );

    if (selectByIndex) {
      return selectByIndex.value;
    }

    const selects = Array.from(document.querySelectorAll('select'));

    const selectByName = selects.find(function (select) {
      return normalize(select.name) === normalize(optionName);
    });

    return selectByName ? selectByName.value : null;
  }

  function getPackagingOptionIndex() {
    return productOptions.findIndex(function (optionName) {
      return normalize(optionName) === 'packaging';
    });
  }

  function getCurrentSelections() {
    return productOptions.map(function (optionName, optionIndex) {
      return getSelectedOptionValue(optionName, optionIndex);
    });
  }

  function findVariantForPackaging(packagingValue) {
    const packagingIndex = getPackagingOptionIndex();

    if (packagingIndex === -1) return null;

    const selections = getCurrentSelections();

    return variants.find(function (variant) {
      return variant.options.every(function (variantValue, index) {
        if (index === packagingIndex) {
          return normalize(variantValue) === normalize(packagingValue);
        }

        const selectedValue = selections[index];

        if (
          selectedValue === null ||
          selectedValue === undefined ||
          selectedValue === ''
        ) {
          return true;
        }

        return normalize(variantValue) === normalize(selectedValue);
      });
    }) || null;
  }

  function isStandardActuallyInStock(standardVariant) {
    if (!standardVariant) {
      return false;
    }

    /*
     * If Shopify tracks inventory for Standard, use the real quantity.
     * This overrides "continue selling when out of stock".
     */
    if (standardVariant.inventory_management) {
      return Number(standardVariant.inventory_quantity) > 0;
    }

    /*
     * For variants whose inventory is not tracked, fall back to
     * Shopify's normal availability.
     */
    return standardVariant.available === true;
  }

  function setSignatureState(disabled) {
    const signatureInput = getPackagingInput('Signature Box');

    if (!signatureInput) return;

    const signatureContainer =
      signatureInput.closest('.variant-input');

    const signatureLabel = signatureInput.id
      ? document.querySelector(
          'label[for="' + CSS.escape(signatureInput.id) + '"]'
        )
      : null;

    signatureInput.disabled = disabled;
    signatureInput.setAttribute(
      'aria-disabled',
      disabled ? 'true' : 'false'
    );

    if (signatureContainer) {
      signatureContainer.classList.toggle(
        'packaging-force-sold-out',
        disabled
      );
    }

    if (signatureLabel) {
      signatureLabel.classList.toggle('disabled', disabled);
      signatureLabel.classList.toggle('sold-out', disabled);
      signatureLabel.setAttribute(
        'aria-disabled',
        disabled ? 'true' : 'false'
      );
    }

    /*
     * If Signature is selected while Standard is out of stock,
     * automatically select Standard so the product displays SOLD OUT.
     */
    if (disabled && signatureInput.checked) {
      const standardInput = getPackagingInput('Standard');

      if (standardInput) {
        standardInput.checked = true;

        standardInput.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      }
    }
  }

  function updatePackagingDependency() {
    if (isProcessing) return;

    isProcessing = true;

    try {
      const standardVariant =
        findVariantForPackaging('Standard');

      const standardInStock =
        isStandardActuallyInStock(standardVariant);

      setSignatureState(!standardInStock);
    } finally {
      isProcessing = false;
    }
  }

  function scheduleUpdate() {
    clearTimeout(updateTimeout);

    updateTimeout = setTimeout(function () {
      updatePackagingDependency();
    }, 150);
  }

  document.addEventListener('change', function (event) {
    if (
      event.target.matches(
        '[data-variant-input], ' +
        '.variant-input input, ' +
        '.variant-input-wrap input, ' +
        'select[data-index]'
      )
    ) {
      scheduleUpdate();
    }
  });

  /*
   * Some themes rebuild variant buttons after changing colour,
   * length or another option.
   */
  const observer = new MutationObserver(function (mutations) {
    const relevantChange = mutations.some(function (mutation) {
      return mutation.type === 'childList';
    });

    if (relevantChange) {
      scheduleUpdate();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  updatePackagingDependency();
});